import {
  AXIS_TYPE,
  DEFAULT_X_CHART_TYPE_PARAM,
  DEFAULT_Y_CHART_TYPE_PARAM,
  DEFAULT_Y_SYSTEM_CHART_TYPE_PARAM,
  IS_BOX_PLOT_CHART,
  IS_MOSAIC_CHART,
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART,
  IS_TREND_DATE_CHART
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import isEqual from "lodash/isEqual";
import { getAxisDataType } from "utils/getAxisDataType";

export function handleFeaturesOnChartTypeSwitch(
  ct: EvaChart,
  currentChartType: EvaChart,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sessionDataObj: any
) {
  // Remove chartTypeParam for certain chart types or if x/y property is ""
  if (
    IS_TREND_DATE_CHART(ct) ||
    IS_MOSAIC_CHART(ct) ||
    IS_STACKED_BAR_CHART(ct) ||
    // This chart's x/y roles are swapped from StackedBar's — force a reset on
    // either side of the switch so a stale category value doesn't land in the
    // numeric slot (or vice versa) instead of this chart type's own default.
    IS_STACKED_BAR_HORIZONTAL_CHART(ct) ||
    IS_TREND_DATE_CHART(currentChartType) ||
    IS_MOSAIC_CHART(currentChartType) ||
    IS_STACKED_BAR_CHART(currentChartType) ||
    IS_STACKED_BAR_HORIZONTAL_CHART(currentChartType) ||
    // switching to chartTypeParam chart from chart without axis selectors
    sessionDataObj?.features?.chartTypeParam?.properties?.x?.property === "" ||
    sessionDataObj?.features?.chartTypeParam?.properties?.y?.property === "" ||
    // switching from a chart that's on a newly added dashboard with old chart type params
    isEqual(
      sessionDataObj?.features?.chartTypeParam?.properties?.x,
      DEFAULT_X_CHART_TYPE_PARAM
    ) ||
    isEqual(
      sessionDataObj?.features?.chartTypeParam?.properties?.y,
      DEFAULT_Y_CHART_TYPE_PARAM
    ) ||
    // switching from a chart that's on a newly added system dashboard with system dashboard chart type params
    isEqual(
      sessionDataObj?.features?.chartTypeParam?.properties?.y,
      DEFAULT_Y_SYSTEM_CHART_TYPE_PARAM
    )
  ) {
    if (sessionDataObj?.features?.chartTypeParam) {
      delete sessionDataObj.features.chartTypeParam;
    }
  }

  // Axis swap logic for CrossPlot, Probit, BoxPlot
  const isCrossProbitBox =
    ct === EvaChart.CrossPlot || ct === EvaChart.Probit || ct === EvaChart.BoxPlot;
  const isCurrentCrossOrBox =
    currentChartType === EvaChart.CrossPlot || currentChartType === EvaChart.BoxPlot;
  const isCurrentProbit = currentChartType === EvaChart.Probit;

  if (isCrossProbitBox && sessionDataObj?.features?.chartTypeParam?.properties) {
    const props = sessionDataObj.features.chartTypeParam.properties;
    // Swap axes when switching between CrossPlot/BoxPlot and Probit
    if (
      (isCurrentCrossOrBox && ct === EvaChart.Probit) ||
      ((ct === EvaChart.CrossPlot || ct === EvaChart.BoxPlot) && isCurrentProbit)
    ) {
      const temp = props.x;
      props.x = props.y;
      props.y = temp;
    }
  }

  if (sessionDataObj?.features?.chartTypeParam?.properties) {
    const props = sessionDataObj.features.chartTypeParam.properties;

    ([AXIS_TYPE.x, AXIS_TYPE.y] as const).forEach((axis) => {
      // A stored snapshot can carry a chartTypeParam whose `properties` object is
      // present but missing an axis entirely — `properties` is truthy so the guard
      // above lets us in, then `props.x` is undefined. Reading through it threw a
      // TypeError that escaped the chart-type menu's onClick before it could
      // dispatch, so the chart silently refused to switch (EVA-155). Nothing to
      // filter on an axis that was never set: skip it.
      if (!props[axis]) return;
      // Remove axis properties if dataType doesn't match available types
      if (
        // eslint-disable-next-line no-prototype-builtins
        props[axis].hasOwnProperty("dataType") &&
        !getAxisDataType(axis, ct).includes(props[axis].dataType) &&
        // boxplot accepts all fields
        !IS_BOX_PLOT_CHART(ct)
      ) {
        delete props[axis];
      }
    });
  }

  // Always remove datatable feature
  if (sessionDataObj?.features?.datatable) {
    delete sessionDataObj.features.datatable;
  }

  // EVA-429: the box plot ranks its boxes by the median of the y field, the stacked bars
  // rank their category axis by a bar total. Same feature, different question, so a sort
  // carried across that boundary is not the choice the user made.
  if (
    IS_BOX_PLOT_CHART(ct) !== IS_BOX_PLOT_CHART(currentChartType) &&
    sessionDataObj?.features?.xAxisSort
  ) {
    delete sessionDataObj.features.xAxisSort;
  }

  return sessionDataObj;
}
